// Armazenamento gerido pela plataforma (ADR 0021): pools do administrador e
// atribuição por organização com quota. Só administrador da plataforma entra.
import { ArrowRight, Cloud, Copy, Fingerprint, Gauge, HardDrive, Hourglass, Info, Pencil, Plus, Server, Trash2, TriangleAlert, Wifi } from "lucide-react";
import { Tabs } from "radix-ui";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { useAdminOrgPools, useAdminPools, useInvalidateAdmin, useOrgs, useSession } from "../api/queries";
import type { TLSCertificateInfo, OrgPool, PoolInput, StoragePool } from "../api/types";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { ErrorState, LoadingRows, PageHead } from "../ui/Common";
import { Dialog } from "../ui/Dialog";
import { HealthPill, Pill } from "../ui/Pills";
import { useToast } from "../ui/Toasts";
import { adminStorageMessages } from "./adminStorage.i18n";

const GB = 1e9;

/** Backends que o contrato aceita criar e alterar pela API (PoolInput). */
type ExternalBackend = "s3" | "sftp" | "webdav" | "interserver";
type PoolRole = StoragePool["role"];
/** Limites padrão de um teste gratuito; o servidor aplica os mesmos quando faltam (ADR 0038). */
const TRIAL_DEFAULTS = { quotaBytes: 100e9, machineLimit: 5, days: 30, graceDays: 15 };
const EXTERNAL: ExternalBackend[] = ["s3", "sftp", "webdav", "interserver"];

const backendIcon = (b: StoragePool["backend"]) => (b === "local" ? HardDrive : b === "sftp" || b === "webdav" || b === "interserver" ? Server : Cloud);
const errorText = (err: unknown, fallback: string) => (err instanceof ApiRequestError ? err.message : fallback);
/** Capacidade que vale para conferir superlotação: a informada, ou a medida. */
const capacityOf = (p: StoragePool) => p.capacityBytes ?? p.detectedCapacityBytes ?? null;

export function AdminStoragePage() {
  const session = useSession();
  const t = useT(adminStorageMessages);

  if (session.isPending) return <div className="panel"><LoadingRows rows={6} /></div>;
  if (session.data?.user.platformAdmin !== true) {
    return (
      <>
        <PageHead title={t.title} />
        <div className="callout crit" role="alert"><Info aria-hidden="true" /><span>{t.forbidden}</span></div>
      </>
    );
  }

  return (
    <>
      <PageHead title={t.title} lede={t.lede} />
      <section className="panel">
        <Tabs.Root defaultValue="pools">
          <Tabs.List className="tabs-list" aria-label={t.tabs}>
            <Tabs.Trigger value="pools">{t.tabPools}</Tabs.Trigger>
            <Tabs.Trigger value="assignments">{t.tabAssignments}</Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="pools"><PoolsTab /></Tabs.Content>
          <Tabs.Content value="assignments"><AssignmentsTab /></Tabs.Content>
        </Tabs.Root>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Pools da plataforma

function PoolsTab() {
  const pools = useAdminPools();
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateAdmin();
  const t = useT(adminStorageMessages);
  const tc = useT(common);
  const f = useFormat();
  const [editing, setEditing] = useState<StoragePool | "new" | null>(null);
  const [removing, setRemoving] = useState<StoragePool | null>(null);
  const [replicaFor, setReplicaFor] = useState<StoragePool | null>(null);
  const [probing, setProbing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function probe(p: StoragePool) {
    setProbing(p.id);
    try {
      const r = await api.adminProbePool(p.id);
      toast("ok", r.detectedCapacityBytes === null ? t.measuredNone : t.measured(f.bytes(r.detectedCapacityBytes), f.bytes(r.detectedFreeBytes ?? 0)));
      invalidate();
    } catch (err) {
      toast("error", errorText(err, t.failed));
    } finally {
      setProbing(null);
    }
  }

  async function remove() {
    if (!removing) return;
    setBusy(true);
    try {
      await api.adminDeletePool(removing.id);
      toast("ok", t.removed);
      setRemoving(null);
      invalidate();
    } catch (err) {
      toast("error", errorText(err, t.failed));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="panel-head" style={{ borderBottom: 0 }}>
        <span className="sub">{t.poolsSub}</span>
        <button className="btn btn-sm" type="button" onClick={() => setEditing("new")}><Plus aria-hidden="true" /> {t.newPool}</button>
      </div>

      {pools.isPending ? <LoadingRows /> : pools.isError ? <div className="panel-body"><ErrorState error={pools.error} /></div> : pools.data.length === 0 ? (
        <div className="empty">{t.noPools}</div>
      ) : (
        <div className="pool-cards panel-body" style={{ paddingTop: 0 }}>
          {pools.data.map((p) => {
            const Icon = backendIcon(p.backend);
            const replica = pools.data.find((x) => x.id === p.replicaPoolId);
            const capacity = capacityOf(p);
            const assigned = p.assignedQuotaBytes ?? null;
            const editable = p.backend !== "local";
            return (
              <section className="panel pool-card" key={p.id}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <span className="backend-badge"><Icon aria-hidden="true" /></span>
                  <div style={{ minWidth: 0, flex: 1, display: "grid", gap: 4 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <h2>{p.name}</h2>
                      <Pill tone="info" icon={p.role === "replica" ? ArrowRight : p.role === "trial" ? Hourglass : HardDrive}>{p.role === "replica" ? t.replica : p.role === "trial" ? t.trial : t.primary}</Pill>
                      <HealthPill health={p.health} />
                    </div>
                    <span className="ink-2" style={{ fontSize: "var(--fs-12)" }}>{tc.backend[p.backend]}</span>
                    <span className="pool-loc">{p.location}</span>
                  </div>
                  <div style={{ display: "flex", gap: 2 }}>
                    <button
                      className="btn btn-ghost btn-icon" type="button" disabled={!editable}
                      title={editable ? t.edit : t.localReadOnly} aria-label={`${t.edit} ${p.name}`}
                      onClick={() => setEditing(p)}
                    >
                      <Pencil aria-hidden="true" />
                    </button>
                    <button className="btn btn-ghost btn-icon btn-danger" type="button" title={t.remove} aria-label={`${t.remove} ${p.name}`} onClick={() => setRemoving(p)}>
                      <Trash2 aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="stats">
                  <div className="stat"><span className="v">{p.capacityBytes === null ? t.unknown : f.bytes(p.capacityBytes)}</span><span className="k">{t.capacityInformed}</span></div>
                  <div className="stat"><span className="v">{p.detectedCapacityBytes == null ? t.notMeasured : f.bytes(p.detectedCapacityBytes)}</span><span className="k">{t.capacityDetected}</span></div>
                  <div className="stat"><span className="v">{f.bytes(p.usedBytes)}</span><span className="k">{t.used}</span></div>
                </div>
                <div className="stats">
                  <div className="stat"><span className="v">{assigned === null ? (p.orgCount ? t.noLimit : t.unknown) : f.bytes(assigned)}</span><span className="k">{t.assignedQuota}</span></div>
                  <div className="stat"><span className="v">{f.int(p.orgCount ?? 0)}</span><span className="k">{t.orgs}</span></div>
                  <div className="stat"><span className="v">{p.detectedFreeBytes == null ? t.unknown : f.bytes(p.detectedFreeBytes)}</span><span className="k">{t.freeAtDestination}</span></div>
                </div>

                {assigned !== null && capacity !== null && assigned > capacity && (
                  <div className="callout warn"><TriangleAlert aria-hidden="true" /><span>{t.overcommitted(f.bytes(assigned), f.bytes(capacity))}</span></div>
                )}
                {p.lastError && <div className="callout crit"><Info aria-hidden="true" /><span>{t.lastError(p.lastError)}</span></div>}
                {replica && (
                  <div className="pool-link">
                    <ArrowRight aria-hidden="true" /> {t.replicatedTo} <strong>{replica.name}</strong>
                    {replica.replicationLagSeconds !== null && <> · {t.replicationLag(f.duration(replica.replicationLagSeconds))}</>}
                  </div>
                )}
                <div className="muted" style={{ fontSize: "var(--fs-12)" }}>{p.probedAt ? t.probedAt(f.ago(p.probedAt)) : t.neverProbed}</div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn btn-sm" type="button" onClick={() => void probe(p)} disabled={probing !== null}>
                    <Gauge aria-hidden="true" /> {probing === p.id ? t.measuring : t.measure}
                  </button>
                  {p.role === "primary" && pools.data.some((x) => x.role === "replica") && (
                    <button className="btn btn-sm btn-ghost" type="button" onClick={() => setReplicaFor(p)}>
                      <Copy aria-hidden="true" /> {p.replicaPoolId ? t.changeRedundancy : t.setRedundancy}
                    </button>
                  )}
                </div>
                {p.capacityBytes === null && <span style={{ fontSize: "var(--fs-12)", color: "var(--ink-3)" }}>{t.capacityFromServer}</span>}
                {!editable && <span style={{ fontSize: "var(--fs-12)", color: "var(--ink-3)" }}>{t.localReadOnly}</span>}
              </section>
            );
          })}
        </div>
      )}

      {replicaFor && pools.data && (
        <ReplicaDialog primary={replicaFor} replicas={pools.data.filter((x) => x.role === "replica")} onClose={(changed) => { setReplicaFor(null); if (changed) invalidate(); }} />
      )}
      {editing && <PoolDialog pool={editing === "new" ? null : editing} onClose={(changed) => { setEditing(null); if (changed) invalidate(); }} />}
      <Dialog
        open={removing !== null}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={removing ? t.removeTitle(removing.name) : ""}
        description={t.removeDescription}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setRemoving(null)}>{tc.cancel}</button>
            <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={() => void remove()} disabled={busy}>{t.remove}</button>
          </>
        }
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Atribuições por organização

function AssignmentsTab() {
  const { orgId = "" } = useParams();
  const orgs = useOrgs();
  const [org, setOrg] = useState(orgId);
  const rows = useAdminOrgPools(org);
  const pools = useAdminPools();
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateAdmin();
  const t = useT(adminStorageMessages);
  const tc = useT(common);
  const f = useFormat();
  const [editingRow, setEditingRow] = useState<OrgPool | "new" | null>(null);
  const [removing, setRemoving] = useState<OrgPool | null>(null);
  const [busy, setBusy] = useState(false);

  const orgName = orgs.data?.find((o) => o.id === org)?.name ?? org;
  // Sem a lista carregada não dá para saber o que já está atribuído: atribuir
  // aqui sobrescreveria a quota existente (PUT é upsert no servidor).
  const loaded = rows.data !== undefined;
  const assignedIds = new Set(rows.data?.map((r) => r.poolId));
  // Pool de teste (ADR 0038) também pode ser atribuído à mão, para um teste montado pela equipe.
  const choices = loaded ? (pools.data ?? []).filter((p) => (p.role === "primary" || p.role === "trial") && !assignedIds.has(p.id)) : [];
  // Superlotação: a soma das quotas do pool passou da capacidade (ADR 0021).
  const crowded = (pools.data ?? []).filter((p) => {
    const capacity = capacityOf(p);
    return assignedIds.has(p.id) && p.assignedQuotaBytes != null && capacity !== null && p.assignedQuotaBytes > capacity;
  });

  async function remove() {
    if (!removing) return;
    setBusy(true);
    try {
      await api.adminRemoveOrgPool(org, removing.poolId);
      toast("ok", t.assignmentRemoved);
      setRemoving(null);
      invalidate(org);
    } catch (err) {
      toast("error", errorText(err, t.failed));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="panel-head" style={{ borderBottom: 0 }}>
        <span className="sub">{t.assignmentsSub}</span>
      </div>
      <div className="panel-body" style={{ paddingTop: 0, display: "grid", gap: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="field" style={{ flex: "1 1 220px", minWidth: 0 }}>
            <label htmlFor="assign-org">{t.organization}</label>
            <select id="assign-org" className="select" value={org} onChange={(e) => setOrg(e.target.value)}>
              {orgs.data?.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" type="button" onClick={() => setEditingRow("new")} disabled={org === "" || !loaded || rows.isFetching || choices.length === 0}>
            <Plus aria-hidden="true" /> {t.assignPool}
          </button>
        </div>

        {crowded.map((p) => (
          <div className="callout warn" key={p.id}>
            <TriangleAlert aria-hidden="true" />
            <span>{p.name} · {t.overcommitted(f.bytes(p.assignedQuotaBytes ?? 0), f.bytes(capacityOf(p) ?? 0))}</span>
          </div>
        ))}
      </div>

      {rows.isPending ? <LoadingRows /> : rows.isError ? <div className="panel-body"><ErrorState error={rows.error} /></div> : rows.data.length === 0 ? (
        <div className="empty">{t.noAssignments}</div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>{t.colPool}</th><th style={{ width: "28%" }}>{t.colQuota}</th><th>{t.colUsed}</th><th>{t.colFree}</th>
                <th className="right">{t.colMachines}</th><th><span className="sr-only">{t.colActions}</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.data.map((r) => {
                const free = r.quotaBytes === null ? null : Math.max(0, r.quotaBytes - r.usedBytes);
                return (
                  <tr key={r.poolId}>
                    <td>
                      <strong>{r.poolName}</strong>
                      {r.state === "draining" && <> <Pill tone="warn">{t.draining}</Pill></>}
                    </td>
                    <td>{r.quotaBytes === null ? t.noLimit : f.bytes(r.quotaBytes)}</td>
                    <td className="num">{f.bytes(r.usedBytes)}</td>
                    <td className="num">{free === null ? t.noLimit : f.bytes(free)}</td>
                    <td className="right num">{f.int(r.repositories)}</td>
                    <td className="right">
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                        <button className="btn btn-sm" type="button" onClick={() => setEditingRow(r)}>{t.changeQuota}</button>
                        <button className="btn btn-sm btn-ghost btn-danger" type="button" onClick={() => setRemoving(r)}>{t.removeAssignment}</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingRow && (
        <AssignmentDialog
          org={org}
          orgName={orgName}
          row={editingRow === "new" ? null : editingRow}
          choices={choices}
          onClose={(changed) => { setEditingRow(null); if (changed) invalidate(org); }}
        />
      )}
      <Dialog
        open={removing !== null}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={removing ? t.removeAssignmentTitle(removing.poolName) : ""}
        description={t.removeAssignmentDescription}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setRemoving(null)}>{tc.cancel}</button>
            <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={() => void remove()} disabled={busy}>{t.removeAssignment}</button>
          </>
        }
      />
    </>
  );
}

function AssignmentDialog({ org, orgName, row, choices, onClose }: {
  org: string; orgName: string; row: OrgPool | null; choices: StoragePool[]; onClose: (changed: boolean) => void;
}) {
  const api = useApi();
  const toast = useToast();
  const t = useT(adminStorageMessages);
  const tc = useT(common);
  const [poolId, setPoolId] = useState(row?.poolId ?? choices[0]?.id ?? "");
  const [noLimit, setNoLimit] = useState(row ? row.quotaBytes === null : false);
  const [gb, setGb] = useState(row?.quotaBytes ? String(Math.round(row.quotaBytes / GB)) : "1000");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const quota = noLimit ? null : Math.round(Number(gb) * GB);
  const valid = poolId !== "" && (noLimit || (Number.isFinite(Number(gb)) && Number(gb) > 0));

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.adminSetOrgPool(org, poolId, { quotaBytes: quota });
      toast("ok", row ? t.quotaSaved : t.assigned);
      onClose(true);
    } catch (err) {
      setError(errorText(err, t.failed));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose(false)}
      title={row ? t.quotaTitle(row.poolName) : t.assignTitle(orgName)}
      description={row ? t.quotaDescription : t.assignDescription}
      footer={
        <>
          <button className="btn" type="button" onClick={() => onClose(false)}>{tc.cancel}</button>
          <button className="btn btn-primary" type="button" onClick={() => void save()} disabled={busy || !valid}>{busy ? t.saving : t.save}</button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 14 }}>
        {!row && (
          <div className="field">
            <label htmlFor="assign-pool">{t.assignPoolField}</label>
            {choices.length === 0 ? (
              <span className="hint">{t.assignNoneLeft}</span>
            ) : (
              <select id="assign-pool" className="select" value={poolId} onChange={(e) => setPoolId(e.target.value)}>
                {choices.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>
        )}
        <div className="field">
          <label htmlFor="assign-quota">{t.quotaField}</label>
          <input id="assign-quota" className="input num" type="number" min={1} value={noLimit ? "" : gb} disabled={noLimit} onChange={(e) => setGb(e.target.value)} />
          <label className="check" htmlFor="assign-nolimit">
            <input id="assign-nolimit" type="checkbox" checked={noLimit} onChange={(e) => setNoLimit(e.target.checked)} />
            <span>{t.quotaNoLimit}</span>
          </label>
          {error && <p className="field error" role="alert">{error}</p>}
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Diálogos do pool

function ReplicaDialog({ primary, replicas, onClose }: { primary: StoragePool; replicas: StoragePool[]; onClose: (changed: boolean) => void }) {
  const t = useT(adminStorageMessages);
  const tc = useT(common);
  const api = useApi();
  const toast = useToast();
  const [choice, setChoice] = useState(primary.replicaPoolId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.adminSetPoolReplica(primary.id, choice || null);
      toast("ok", t.redundancySaved);
      onClose(true);
    } catch (err) {
      setError(errorText(err, t.failed));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose(false)}
      title={t.redundancyTitle(primary.name)}
      description={t.redundancyDescription}
      footer={
        <>
          <button className="btn" type="button" onClick={() => onClose(false)}>{tc.cancel}</button>
          <button className="btn btn-primary" type="button" onClick={() => void save()} disabled={busy}>{t.save}</button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="replica-choice">{t.redundancyPool}</label>
        <select id="replica-choice" className="select" value={choice} onChange={(e) => setChoice(e.target.value)}>
          <option value="">{t.noRedundancy}</option>
          {replicas.map((x) => <option key={x.id} value={x.id}>{x.name} · {tc.backend[x.backend]}</option>)}
        </select>
        {error && <p className="field error" role="alert">{error}</p>}
      </div>
    </Dialog>
  );
}

function PoolDialog({ pool, onClose }: { pool: StoragePool | null; onClose: (changed: boolean) => void }) {
  const t = useT(adminStorageMessages);
  const tc = useT(common);
  const api = useApi();
  const toast = useToast();
  const f = useFormat();
  const s3 = pool?.settings?.s3;
  const sftp = pool?.settings?.sftp;
  const dav = pool?.settings?.webdav;
  const is = pool?.settings?.interserver;

  const [backend, setBackend] = useState<ExternalBackend>((pool?.backend as ExternalBackend | undefined) ?? "s3");
  const [name, setName] = useState(pool?.name ?? t.defaultName);
  // Capacidade contratada do destino, em GB decimais; vazio significa desconhecida.
  const [capacityGB, setCapacityGB] = useState(pool?.capacityBytes != null ? String(Math.round(pool.capacityBytes / 1e9)) : "");
  const [role, setRole] = useState<PoolRole>(pool?.role ?? "primary");
  // Limites de cada teste gratuito neste pool (ADR 0038); valem só com o papel "trial".
  const trial = pool?.settings?.trial;
  const [trialQuotaGB, setTrialQuotaGB] = useState(String(Math.round((trial?.quotaBytes ?? TRIAL_DEFAULTS.quotaBytes) / 1e9)));
  const [trialMachines, setTrialMachines] = useState(String(trial?.machineLimit ?? TRIAL_DEFAULTS.machineLimit));
  const [trialDays, setTrialDays] = useState(String(trial?.days ?? TRIAL_DEFAULTS.days));
  const [trialGrace, setTrialGrace] = useState(String(trial?.graceDays ?? TRIAL_DEFAULTS.graceDays));
  const [endpoint, setEndpoint] = useState(s3?.endpoint ?? "");
  const [region, setRegion] = useState(s3?.region ?? "");
  const [bucket, setBucket] = useState(s3?.bucket ?? "");
  const [prefix, setPrefix] = useState(s3?.prefix ?? "");
  const [accessKeyId, setAccessKeyId] = useState(s3?.accessKeyId ?? "");
  const [secretAccessKey, setSecretAccessKey] = useState("");
  const [host, setHost] = useState(sftp?.host ?? "");
  const [port, setPort] = useState(sftp?.port ?? 22);
  const [username, setUsername] = useState(sftp?.username ?? dav?.username ?? is?.username ?? "");
  const [path, setPath] = useState(sftp?.path ?? is?.path ?? "/");
  const [knownHosts, setKnownHosts] = useState(sftp?.knownHosts ?? "");
  const [fingerprint, setFingerprint] = useState<{ fp: string; type: string } | null>(null);
  // Certificado fixado do WebDAV (ADR 0034): a sonda mostra o certificado; a pessoa confere e confia.
  const [certificateSha256, setCertificateSha256] = useState(dav?.certificateSha256 ?? is?.certificateSha256 ?? "");
  const [certInfo, setCertInfo] = useState<TLSCertificateInfo | null>(null);
  const [hostKeyOk, setHostKeyOk] = useState(Boolean(sftp?.knownHosts));
  const [password, setPassword] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [url, setUrl] = useState(dav?.url ?? is?.url ?? "");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<"test" | "save" | "key" | null>(null);

  function input(): PoolInput {
    const settings: PoolInput["settings"] =
      backend === "s3" ? { s3: { endpoint, region, bucket, prefix, accessKeyId } } :
      backend === "sftp" ? { sftp: { host, port, username, path, knownHosts } } :
      backend === "webdav" ? { webdav: { url, username, certificateSha256 } } :
      { interserver: { url, username, path: path.replace(/^\/+/, "") || "e-backup", certificateSha256 } };
    if (role === "trial") {
      settings.trial = {
        quotaBytes: Math.round((Number(trialQuotaGB.replace(",", ".")) || 0) * 1e9),
        machineLimit: Math.round(Number(trialMachines) || 0),
        days: Math.round(Number(trialDays) || 0),
        graceDays: Math.round(Number(trialGrace) || 0),
      };
    }
    const gb = Number(capacityGB.replace(",", "."));
    const capacityBytes = capacityGB.trim() === "" || !Number.isFinite(gb) || gb <= 0 ? null : Math.round(gb * 1e9);
    return { name, backend, role, capacityBytes, settings, secrets: { secretAccessKey, password, privateKey } };
  }

  async function run(kind: "test" | "save") {
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      if (kind === "test") {
        await api.adminTestPool({ ...input(), ...(pool ? { poolId: pool.id } : {}) });
        setNotice(t.testOk);
      } else {
        if (pool) await api.adminUpdatePool(pool.id, input());
        else await api.adminCreatePool(input());
        toast("ok", pool ? t.updated : t.created);
        onClose(true);
      }
    } catch (err) {
      setError(errorText(err, t.failed));
    } finally {
      setBusy(null);
    }
  }

  async function fetchHostKey() {
    setBusy("key");
    setError(null);
    try {
      const r = await api.adminSftpHostKey(host, port);
      setKnownHosts(r.knownHosts);
      setFingerprint({ fp: r.fingerprint, type: r.keyType });
      setHostKeyOk(false);
    } catch (err) {
      setError(errorText(err, t.failed));
    } finally {
      setBusy(null);
    }
  }

  async function fetchCertificate() {
    setBusy("key");
    setError(null);
    try {
      setCertInfo(await api.adminTLSCertificate(url));
    } catch (err) {
      setError(errorText(err, t.failed));
    } finally {
      setBusy(null);
    }
  }

  const secretHint = pool ? t.keepSecret : undefined;
  const canSave = name.trim() !== "" && (backend !== "sftp" || (knownHosts.trim() !== "" && hostKeyOk));

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose(false)}
      title={pool ? t.editTitle(pool.name) : t.dialogTitle}
      description={t.dialogDescription}
      footer={
        <>
          <button className="btn" type="button" onClick={() => onClose(false)}>{tc.cancel}</button>
          <button className="btn" type="button" onClick={() => void run("test")} disabled={busy !== null || !canSave}><Wifi aria-hidden="true" /> {busy === "test" ? t.testing : t.testConnection}</button>
          <button className="btn btn-primary" type="button" onClick={() => void run("save")} disabled={busy !== null || !canSave}>{busy === "save" ? t.saving : t.save}</button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 14 }}>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="pool-backend">{t.type}</label>
            <select id="pool-backend" className="select" value={backend} disabled={pool !== null} onChange={(e) => setBackend(e.target.value as ExternalBackend)}>
              {EXTERNAL.map((b) => <option key={b} value={b}>{tc.backend[b]}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="pool-role">{t.role}</label>
            <select id="pool-role" className="select" value={role} onChange={(e) => setRole(e.target.value as PoolRole)}>
              <option value="primary">{t.rolePrimary}</option>
              <option value="replica">{t.roleReplica}</option>
              <option value="trial">{t.roleTrial}</option>
            </select>
          </div>
        </div>
        {role === "trial" && (
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0, display: "grid", gap: 10 }}>
            <legend className="label" style={{ padding: 0 }}>{t.trialLimits}</legend>
            <span className="hint">{t.trialLimitsHint}</span>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="pool-trial-quota">{t.trialQuota}</label>
                <input id="pool-trial-quota" className="input num" inputMode="numeric" value={trialQuotaGB} onChange={(e) => setTrialQuotaGB(e.target.value.replace(/[^0-9.,]/g, ""))} />
              </div>
              <div className="field">
                <label htmlFor="pool-trial-machines">{t.trialMachines}</label>
                <input id="pool-trial-machines" className="input num" inputMode="numeric" value={trialMachines} onChange={(e) => setTrialMachines(e.target.value.replace(/\D/g, ""))} />
              </div>
              <div className="field">
                <label htmlFor="pool-trial-days">{t.trialDays}</label>
                <input id="pool-trial-days" className="input num" inputMode="numeric" value={trialDays} onChange={(e) => setTrialDays(e.target.value.replace(/\D/g, ""))} />
              </div>
              <div className="field">
                <label htmlFor="pool-trial-grace">{t.trialGrace}</label>
                <input id="pool-trial-grace" className="input num" inputMode="numeric" value={trialGrace} onChange={(e) => setTrialGrace(e.target.value.replace(/\D/g, ""))} />
              </div>
            </div>
            <span className="hint">{t.trialCapacityHint}</span>
          </fieldset>
        )}
        {pool === null && <span className="hint">{t.comingSoon}</span>}
        <div className="form-grid">
          <div className="field">
            <label htmlFor="pool-name">{t.name}</label>
            <input id="pool-name" className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pool-capacity">{t.capacityField}</label>
            <input id="pool-capacity" className="input num" inputMode="numeric" value={capacityGB} placeholder={t.capacityPlaceholder}
              onChange={(e) => setCapacityGB(e.target.value.replace(/[^0-9.,]/g, ""))} />
            <span className="hint">{t.capacityHint}</span>
          </div>
        </div>

        {backend === "s3" && (
          <>
            <div className="field">
              <label htmlFor="pool-endpoint">{t.s3Endpoint}</label>
              <input id="pool-endpoint" className="input mono" value={endpoint} placeholder="s3.sa-east-1.amazonaws.com" onChange={(e) => setEndpoint(e.target.value)} />
              <span className="hint">{t.s3EndpointHint}</span>
            </div>
            <div className="form-grid">
              <div className="field"><label htmlFor="pool-bucket">{t.bucket}</label><input id="pool-bucket" className="input mono" value={bucket} onChange={(e) => setBucket(e.target.value)} /></div>
              <div className="field"><label htmlFor="pool-region">{t.region}</label><input id="pool-region" className="input mono" value={region} placeholder="sa-east-1" onChange={(e) => setRegion(e.target.value)} /></div>
            </div>
            <div className="field"><label htmlFor="pool-prefix">{t.prefix}</label><input id="pool-prefix" className="input mono" value={prefix} placeholder="e-backup" onChange={(e) => setPrefix(e.target.value)} /></div>
            <div className="form-grid">
              <div className="field"><label htmlFor="pool-key">{t.accessKey}</label><input id="pool-key" className="input mono" autoComplete="off" value={accessKeyId} onChange={(e) => setAccessKeyId(e.target.value)} /></div>
              <div className="field">
                <label htmlFor="pool-secret">{t.secret}</label>
                <input id="pool-secret" className="input" type="password" autoComplete="new-password" value={secretAccessKey} placeholder={secretHint} onChange={(e) => setSecretAccessKey(e.target.value)} />
              </div>
            </div>
          </>
        )}

        {backend === "sftp" && (
          <>
            <div className="form-grid">
              <div className="field"><label htmlFor="pool-host">{t.sftpHost}</label><input id="pool-host" className="input mono" value={host} placeholder="backup.example.com" onChange={(e) => { setHost(e.target.value); setHostKeyOk(false); }} /></div>
              <div className="field"><label htmlFor="pool-port">{t.port}</label><input id="pool-port" className="input num" type="number" min={1} max={65535} value={port} onChange={(e) => { setPort(Number(e.target.value)); setHostKeyOk(false); }} /></div>
            </div>
            <div className="form-grid">
              <div className="field"><label htmlFor="pool-user">{t.user}</label><input id="pool-user" className="input" autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} /></div>
              <div className="field"><label htmlFor="pool-path">{t.sftpPath}</label><input id="pool-path" className="input mono" value={path} onChange={(e) => setPath(e.target.value)} /></div>
            </div>
            <div className="field">
              <label htmlFor="pool-password">{t.password}</label>
              <input id="pool-password" className="input" type="password" autoComplete="new-password" value={password} placeholder={secretHint} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="pool-pkey">{t.privateKey}</label>
              <textarea id="pool-pkey" className="textarea mono" rows={3} value={privateKey} placeholder={secretHint ?? "-----BEGIN OPENSSH PRIVATE KEY-----"} onChange={(e) => setPrivateKey(e.target.value)} />
            </div>
            <div className="field">
              <span className="label">{t.hostKey}</span>
              <div style={{ justifySelf: "start" }}>
                <button className="btn btn-sm" type="button" onClick={() => void fetchHostKey()} disabled={busy !== null || host.trim() === ""}><Fingerprint aria-hidden="true" /> {t.fetchHostKey}</button>
              </div>
              {knownHosts && <code className="mono" style={{ fontSize: "var(--fs-12)", wordBreak: "break-all" }}>{knownHosts}</code>}
              {fingerprint && (
                <>
                  <strong className="mono" style={{ fontSize: "var(--fs-12)" }}>{t.hostKeyFingerprint(fingerprint.type, fingerprint.fp)}</strong>
                  <label className="check" htmlFor="pool-hostkey-ok">
                    <input id="pool-hostkey-ok" type="checkbox" checked={hostKeyOk} onChange={(e) => setHostKeyOk(e.target.checked)} />
                    <span>{t.hostKeyConfirm}</span>
                  </label>
                </>
              )}
              <span className="hint">{t.hostKeyHint}</span>
            </div>
          </>
        )}

        {backend === "interserver" && (
          <>
            <p className="hint">{t.interserverIntro}</p>
            <div className="field"><label htmlFor="pool-url">{t.interserverUrl}</label><input id="pool-url" className="input mono" value={url} placeholder="https://storage5000.is.cc:8080" onChange={(e) => { setUrl(e.target.value); setCertInfo(null); }} /><span className="hint">{t.interserverUrlHint}</span></div>
            <div className="form-grid">
              <div className="field"><label htmlFor="pool-user">{t.interserverUser}</label><input id="pool-user" className="input" autoComplete="off" value={username} placeholder="backup@st100000.is.cc" onChange={(e) => setUsername(e.target.value)} /><span className="hint">{t.interserverUserHint}</span></div>
              <div className="field">
                <label htmlFor="pool-password">{t.password}</label>
                <input id="pool-password" className="input" type="password" autoComplete="new-password" value={password} placeholder={secretHint} onChange={(e) => setPassword(e.target.value)} />
              </div>
            </div>
            <div className="field"><label htmlFor="pool-path">{t.interserverPath}</label><input id="pool-path" className="input mono" value={path} placeholder="e-backup" onChange={(e) => setPath(e.target.value)} /><span className="hint">{t.interserverPathHint}</span></div>
            <div className="field">
              <span className="label">{t.serverCertificate}</span>
              <div style={{ justifySelf: "start" }}>
                <button className="btn btn-sm" type="button" onClick={() => void fetchCertificate()} disabled={busy !== null || !url.trim().startsWith("https://")}><Fingerprint aria-hidden="true" /> {t.fetchCertificate}</button>
              </div>
              {certInfo && (
                <div style={{ display: "grid", gap: 4, fontSize: "var(--fs-12)" }}>
                  <span>{t.certSubject(certInfo.subject, certInfo.issuer)}</span>
                  <span className={certInfo.verifyError ? "error" : "ink-2"}>{t.certValidity(f.fullDate(certInfo.notBefore), f.fullDate(certInfo.notAfter))}{certInfo.verifyError ? ` · ${t.certInvalid(certInfo.verifyError)}` : ` · ${t.certValid}`}</span>
                  <code className="mono" style={{ wordBreak: "break-all" }}>SHA-256 {certInfo.sha256}</code>
                  <label className="check" htmlFor="pool-cert-trust">
                    <input id="pool-cert-trust" type="checkbox" checked={certificateSha256 === certInfo.sha256} onChange={(e) => setCertificateSha256(e.target.checked ? certInfo.sha256 : "")} />
                    <span>{t.certTrust}</span>
                  </label>
                </div>
              )}
              {!certInfo && certificateSha256 && (
                <label className="check" htmlFor="pool-cert-keep">
                  <input id="pool-cert-keep" type="checkbox" checked onChange={() => setCertificateSha256("")} />
                  <span>{t.certPinned(certificateSha256.slice(0, 16))}</span>
                </label>
              )}
              <span className="hint">{t.certHint}</span>
            </div>
          </>
        )}

        {backend === "webdav" && (
          <>
            <div className="field"><label htmlFor="pool-url">{t.webdavUrl}</label><input id="pool-url" className="input mono" value={url} placeholder="https://dav.example.com/ebackup" onChange={(e) => { setUrl(e.target.value); setCertInfo(null); }} /></div>
            <div className="form-grid">
              <div className="field"><label htmlFor="pool-user">{t.user}</label><input id="pool-user" className="input" autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} /></div>
              <div className="field">
                <label htmlFor="pool-password">{t.password}</label>
                <input id="pool-password" className="input" type="password" autoComplete="new-password" value={password} placeholder={secretHint} onChange={(e) => setPassword(e.target.value)} />
              </div>
            </div>
            <div className="field">
              <span className="label">{t.serverCertificate}</span>
              <div style={{ justifySelf: "start" }}>
                <button className="btn btn-sm" type="button" onClick={() => void fetchCertificate()} disabled={busy !== null || !url.trim().startsWith("https://")}><Fingerprint aria-hidden="true" /> {t.fetchCertificate}</button>
              </div>
              {certInfo && (
                <div style={{ display: "grid", gap: 4, fontSize: "var(--fs-12)" }}>
                  <span>{t.certSubject(certInfo.subject, certInfo.issuer)}</span>
                  <span className={certInfo.verifyError ? "error" : "ink-2"}>{t.certValidity(f.fullDate(certInfo.notBefore), f.fullDate(certInfo.notAfter))}{certInfo.verifyError ? ` · ${t.certInvalid(certInfo.verifyError)}` : ` · ${t.certValid}`}</span>
                  <code className="mono" style={{ wordBreak: "break-all" }}>SHA-256 {certInfo.sha256}</code>
                  <label className="check" htmlFor="pool-cert-trust">
                    <input id="pool-cert-trust" type="checkbox" checked={certificateSha256 === certInfo.sha256} onChange={(e) => setCertificateSha256(e.target.checked ? certInfo.sha256 : "")} />
                    <span>{t.certTrust}</span>
                  </label>
                </div>
              )}
              {!certInfo && certificateSha256 && (
                <label className="check" htmlFor="pool-cert-keep">
                  <input id="pool-cert-keep" type="checkbox" checked onChange={() => setCertificateSha256("")} />
                  <span>{t.certPinned(certificateSha256.slice(0, 16))}</span>
                </label>
              )}
              <span className="hint">{t.certHint}</span>
            </div>
          </>
        )}

        {notice && <div className="callout ok" role="status"><Info aria-hidden="true" /><span>{notice}</span></div>}
        {error && <p className="field error" role="alert">{error}</p>}
      </div>
    </Dialog>
  );
}
